import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Table,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button
} from 'reactstrap'
import { Spinner } from 'react-bootstrap'
import { API_URL } from './utils/Constants/URLS'
import { useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import html2canvas from 'html2canvas'
import CreateEwayBillModal from './Modals/CreateEwayBillModal'

// --- Helper: format money ---
const money = n =>
  (Number.isFinite(+n) ? Number(n) : 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

// --- Helper: format date/time ---
const formatDateTime = iso => {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    return d.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return iso
  }
}

const PDF_RENDER_WIDTH = 794 // ~A4 width at 96dpi

const preparePdfContainer = (container, pageHTML) => {
  container.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    width: ${PDF_RENDER_WIDTH}px;
    padding: 0;
    margin: 0;
    background: white;
    box-sizing: border-box;
  `
  container.innerHTML = pageHTML
}

// Estimate page height in mm (for pagination decisions — no mid-section cuts)
const measurePageHeightMm = (container, pageHTML, pageWidth, margin) => {
  preparePdfContainer(container, pageHTML)
  const imgWidth = pageWidth - 2 * margin
  return (container.scrollHeight / PDF_RENDER_WIDTH) * imgWidth
}

// Render HTML invoice page → canvas → PDF at FULL A4 width.
// Page body as JPEG (small file); QR overlaid as sharp PNG (scannable).
const captureAndAddPdfPage = async (
  pdf,
  container,
  pageHTML,
  margin,
  pageWidth,
  pageHeight,
  qrImg = ''
) => {
  preparePdfContainer(container, pageHTML)

  const canvas = await html2canvas(container, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff'
  })

  const imgWidth = pageWidth - 2 * margin
  const maxHeight = pageHeight - 2 * margin
  let imgHeight = (canvas.height * imgWidth) / canvas.width

  // Content should already fit via pagination; if not, scale both axes equally (no QR stretch)
  let x = margin
  let drawWidth = imgWidth
  if (imgHeight > maxHeight) {
    const ratio = maxHeight / imgHeight
    imgHeight = maxHeight
    drawWidth = imgWidth * ratio
    x = margin + (imgWidth - drawWidth) / 2
  }

  // Compress page as JPEG — keeps PDF size small (~1–5MB)
  pdf.addImage(
    canvas.toDataURL('image/jpeg', 0.9),
    'JPEG',
    x,
    margin,
    drawWidth,
    imgHeight
  )

  // Overlay original QR PNG on top so scanners get crisp modules (not JPEG-blurred)
  if (qrImg) {
    const qrEl = container.querySelector('[data-einvoice-qr]')
    if (qrEl) {
      const cRect = container.getBoundingClientRect()
      const qRect = qrEl.getBoundingClientRect()
      const containerW = container.offsetWidth || PDF_RENDER_WIDTH
      const containerH = container.scrollHeight || 1
      const pdfQrX = x + ((qRect.left - cRect.left) / containerW) * drawWidth
      const pdfQrY = margin + ((qRect.top - cRect.top) / containerH) * imgHeight
      const pdfQrSize = (qrEl.offsetWidth / containerW) * drawWidth
      if (pdfQrSize > 0) {
        pdf.addImage(qrImg, 'PNG', pdfQrX, pdfQrY, pdfQrSize, pdfQrSize)
      }
    }
  }
}

const ViewInvoice = () => {
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : null

  // if you ever want to filter by store, you can use this param
  const { storeid } = useParams()

  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [openId, setOpenId] = useState('')

  // cancel modal states
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelInvoiceId, setCancelInvoiceId] = useState(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelError, setCancelError] = useState(null)

  // convert to e-invoice modal states
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [convertInvoiceNumber, setConvertInvoiceNumber] = useState('')
  const [convertInvoiceId, setConvertInvoiceId] = useState(null)
  const [convertLoading, setConvertLoading] = useState(false)
  const [convertError, setConvertError] = useState(null)
  const [pageSize, setPageSize] = useState(10)
  const [page, setPage] = useState(1)

  // e-way bill modal states
  const [ewayBillModalOpen, setEwayBillModalOpen] = useState(false)
  const [selectedInvoiceForEwayBill, setSelectedInvoiceForEwayBill] = useState(null)

  // credit note modal states
  const [crnModalOpen, setCrnModalOpen] = useState(false)
  const [crnInvoice, setCrnInvoice] = useState(null)
  const [crnLoading, setCrnLoading] = useState(false)
  const [crnError, setCrnError] = useState(null)
  const [crnSuccess, setCrnSuccess] = useState(null)
  // credit note line-item selection: { [slNo]: { selected: bool, qty: number, maxQty: number, item: object } }
  const [crnLineItems, setCrnLineItems] = useState({})

  // sync tally state: { [invoiceId]: { loading: bool, error: string|null, success: string|null } }
  const [syncTallyState, setSyncTallyState] = useState({})

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 1000)
    return () => clearTimeout(t)
  }, [search])

  const totalPages = Math.max(1, Math.ceil(invoices.length / pageSize))

  // reset to page 1 when debounced search or page size changes
  useEffect(() => { setPage(1) }, [debouncedSearch, pageSize])
  // clamp page if results shrink
  useEffect(() => { if (page > totalPages) setPage(totalPages) }, [page, totalPages])

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize
    return invoices.slice(start, start + pageSize)
  }, [invoices, page, pageSize])

  const pageNums = () => {
    const delta = 2
    const start = Math.max(1, page - delta)
    const end = Math.min(totalPages, page + delta)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }
  const handleDownloadInvoicePdf = async (inv, copy) => {
    const gstReq = inv.gstzenRequestPayload || {}
    const seller = gstReq.SellerDtls || {}
    const buyer = gstReq.BuyerDtls || {}
    const ship = gstReq.ShipDtls || gstReq.DispDtls || {}
    const val = gstReq.ValDtls || {}
    const items = Array.isArray(gstReq.ItemList) ? gstReq.ItemList : []
    const totalAmount = items.reduce((sum, it) => {
      const qty = Number(it.Qty || 0)
      const rate = Number(it.UnitPrice || 0)
      const baseAmount = qty * rate
      const discount = Number(it.Discount || 0)
      return sum + (baseAmount - discount)
    }, 0)
    const pickItems = inv.pickList?.lineItems || []
    const hasDiscount = pickItems.some(it => Number(it?.discount || 0) > 0)
    const isProforma = inv.isProformaInvoice === true
    const poNumber = inv?.salesOrder?.customerPONumber || inv?.salesOrder?.PODetails
    const billingAdd = (buyer?.Loc && buyer?.Pin)
      ? `${buyer.Loc}`
      : '-';
    const billingPin = buyer?.Pin || '-';
    const shippingAdd =
      `${gstReq?.ShipDtls?.Addr1 || ''}` || '-';
    const shippingPin = ship?.Pin || '-';
    const poDate = inv.salesOrder?.customerPODate
    const soNo = inv.salesOrder?.salesOrderNumber || '-'
    const priceBasis = inv.salesOrder?.priceBasis || '-'
    const irn = inv.irn || inv.gstzenResponse?.Irn || '-'
    const ackNo = inv.ackNo || inv.gstzenResponse?.AckNo || '-'
    const formatDate = (date) => {
      if (!date || date === '-') return '-';

      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    };

    const ackDateRaw = inv.ackDate || inv.gstzenResponse?.AckDt || '-';
    const ackDate = formatDate(ackDateRaw);
    const isIGST = Number(val?.IgstVal || inv?.igstAmount || 0) > 0

    // convert number to ₹ format
    const money = n =>
      (Number(n) || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })

    // Convert number to words (Indian Rupees with Paise)
    const numberToWords = num => {
      const ones = [
        '',
        'One',
        'Two',
        'Three',
        'Four',
        'Five',
        'Six',
        'Seven',
        'Eight',
        'Nine'
      ]
      const teens = [
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen'
      ]
      const tens = [
        '',
        '',
        'Twenty',
        'Thirty',
        'Forty',
        'Fifty',
        'Sixty',
        'Seventy',
        'Eighty',
        'Ninety'
      ]

      const convertLessThanThousand = n => {
        if (n === 0) return ''
        if (n < 10) return ones[n]
        if (n < 20) return teens[n - 10]
        if (n < 100)
          return (
            tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
          )
        return (
          ones[Math.floor(n / 100)] +
          ' Hundred' +
          (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '')
        )
      }

      // Split into rupees and paise
      const rupees = Math.floor(num)
      const paise = Math.round((num - rupees) * 100)

      if (rupees === 0 && paise === 0) return 'Zero Rupees Only'

      const crore = Math.floor(rupees / 10000000)
      const lakh = Math.floor((rupees % 10000000) / 100000)
      const thousand = Math.floor((rupees % 100000) / 1000)
      const hundred = rupees % 1000

      let result = ''

      if (crore > 0) result += convertLessThanThousand(crore) + ' Crore '
      if (lakh > 0) result += convertLessThanThousand(lakh) + ' Lakh '
      if (thousand > 0)
        result += convertLessThanThousand(thousand) + ' Thousand '
      if (hundred > 0) result += convertLessThanThousand(hundred)

      // Add "Rupees" part
      let finalResult = result.trim()
      if (finalResult) {
        finalResult += ' Rupees'
      } else {
        finalResult = 'Zero Rupees'
      }

      // Add "Paise" part if present
      if (paise > 0) {
        finalResult += ' and ' + convertLessThanThousand(paise) + ' Paise'
      }

      return finalResult + ' Only'
    }

    // Generate QR Code Image — large + quiet zone so scanners can read dense e-invoice payload
    let qrImg = ''
    if (inv.gstzenResponse?.SignedQRCode) {
      qrImg = await QRCode.toDataURL(String(inv.gstzenResponse.SignedQRCode).trim(), {
        errorCorrectionLevel: 'L',
        type: 'image/png',
        margin: 4,
        width: 400,
        color: { dark: '#000000', light: '#FFFFFF' }
      })
    }

    // Group items by HSN code for GST breakup
    const groupedByHsn = items.reduce((acc, it) => {
      const hsn = it.HsnCd ? String(it.HsnCd).trim() : '-'
      if (!acc[hsn]) {
        acc[hsn] = {
          hsnCode: hsn,
          taxableValue: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          igstAmt: 0,
          gstRate: it.GstRt || 0
        }
      }
      const itemTotal = it.AssAmt
      acc[hsn].taxableValue += itemTotal
      acc[hsn].cgstAmt += Number(it.CgstAmt || 0)
      acc[hsn].sgstAmt += Number(it.SgstAmt || 0)
      acc[hsn].igstAmt += Number(it.IgstAmt || 0)
      return acc
    }, {})

    const hsnGroups = Object.values(groupedByHsn)

    // Calculate net total with tax
    const netTotal = gstReq?.ValDtls?.TotInvVal;
    const round = gstReq?.ValDtls?.RndOffAmt;


    // Helper function to generate header HTML
    const generateHeader = () => `
    <div style="border:1px solid #000; background: white;">
      <!-- Header -->
      <p style="margin:5px 0; font-size:12px; text-align:right">${copy === 'original' ? 'Original for Recipient' : copy === 'duplicate' ? 'Duplicate for Transporter' : copy === 'triplicate' ? 'Triplicate for Supplier' : copy === 'quadruplicate' ? 'Quadruplicate Copy' : ''}</p>
      <div style="text-align:center; padding:8px 20px;">
        <center>
          <h2 style="margin:5px 0">Tax Invoice</h2>
          ${gstReq?.TranDtls?.SupTyp === 'SEZWOP' ? `<p style="margin:5px 0; font-size:14px;">(SUPPLY MEANT FOR EXPORT/SUPPLY TO SEZ UNIT OR SEZ DEVELOPER FOR AUTHORISED OPERATIONS ON PAYMENT OF IGST)</p>` : ''}
        </center>
      </div>
      ${isProforma
        ? ''
        : `
      <div style="display:flex; justify-content:space-between; align-items:center; padding:8px 10px; border-top:1px solid #000; border-bottom:1px solid #000">
        <div style="padding:4px 8px; flex:1; min-width:0; font-size:15px; line-height:1.35;">
          <div style="white-space:nowrap;"><b>IRN:</b> ${String(irn || '-')}</div>
          <div><b>Ack No:</b> ${String(ackNo || '-')}</div>
          <div><b>Ack Date:</b> ${String(ackDate || '-')}</div>
        </div>  

        <div style="flex-shrink:0; padding:4px; text-align:center;">
          <div style="font-size:11px; margin-bottom:2px;">e-Invoice</div>
          ${qrImg
          ? `<img data-einvoice-qr="1" src="${qrImg}" width="180" height="180" style="width:180px;height:180px;image-rendering:pixelated;display:block;border:0;padding:0;margin:0;" />`
          : `<div style="width:180px;height:180px;display:flex;align-items:center;justify-content:center;font-size:10px">QR</div>`
        }
        </div>
      </div>
    `
      } 
          
      <!-- Seller / Invoice Details -->
      <div style="display:flex; border-bottom:1px solid #000; ${isProforma ? 'border-top:1px solid #000;' : ''
      }">
        <div style="width:40%; padding:8px; border-right:1px solid #000">
          <div style="font-size:12px; font-weight:bold">${String(
        seller.LglNm || '-'
      )}</div>
          <div style="font-size:12px">${String(seller.Addr1 || '')}</div>
          <div style="font-size:12px">GSTIN: ${String(
        seller.Gstin || '-'
      )}</div>
          <div style="font-size:12px">CIN: U74140WB2011PTC159722</div>
          <div style="font-size:12px">UDYAM Registration no: UDYAM-WB-09-0000238</div>
        </div>

        <div style="width:60%; padding:8px">
          <table style="width:100%; border-collapse:collapse">
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Invoice No</td>
            <td style="padding:2px 4px; font-size:12px">${String(
        gstReq?.DocDtls?.No || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Invoice Date</td><td style="padding:2px 4px; font-size:12px">${String(
        gstReq?.DocDtls?.Dt || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Customer PO No.</td><td style="padding:2px 4px; font-size:12px">${String(
        poNumber || '-'
      )}</td><tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">
              Customer PO Date
            </td>
            <td style="padding:2px 4px; font-size:12px">
              ${poDate
        ? new Date(poDate).toLocaleDateString('en-GB')
        : '-'
      }
            </td>
          </tr>

            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">SO Id</td><td style="padding:2px 4px; font-size:12px">${String(
        soNo || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Price Basis</td><td style="padding:2px 4px; font-size:12px">${String(
        priceBasis || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Dispatch Doc No.</td><td style="padding:2px 4px; font-size:12px">${String(
        inv.dispatchDocumentNumber || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Dispatched Through</td><td style="padding:2px 4px; font-size:12px">${String(
        inv.dispatchedThrough || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Vehicle Details</td><td style="padding:2px 4px; font-size:12px">${String(
        inv.vehicleNumber || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Other References</td><td style="padding:2px 4px; font-size:12px">${String(
        inv.otherReferences || '-'
      )}</td></tr>
          </table>
        </div>
      </div>

      <!-- Consignee / Buyer -->
      <div style="display:flex; border-bottom:1px solid #000">
        
        <div style="width:50%; padding:8px">
          <div style="font-size:12px; font-weight:bold">Bill To</div>
          <div style="font-size:12px">${String(
        buyer.LglNm || '-'
      )}</div>
          <div style="font-size:12px">${String(billingAdd || '-')}</div>
          <div style="font-size:12px">Pin - ${String(billingPin || '-')}</div>
          <div style="font-size:12px">State Code: ${String(
        buyer.Stcd || '-'
      )}</div>
          <div style="font-size:12px">GSTIN: ${String(buyer.Gstin || '-')}</div>
        </div>

        <div style="width:50%; padding:8px; border-left:1px solid #000">
          <div style="font-size:12px; font-weight:bold">Consignee (Ship To)</div>
          <div style="font-size:12px">${String(
        ship.LglNm || '-'
      )}</div>
          <div style="font-size:12px">${String(shippingAdd || '-')}</div>
          <div style="font-size:12px">Pin - ${String(shippingPin || '-')}</div>
          <div style="font-size:12px">State Code: ${String(
        ship.Stcd || '-'
      )}</div>
          <div style="font-size:12px">GSTIN: ${String(ship.Gstin || '-')}</div>
        </div>

      </div>
    </div>
  `

    // Create a temporary container for rendering
    const container = document.createElement('div')
    document.body.appendChild(container)

    try {
      // Import jsPDF dynamically if not already available
      const { jsPDF } = window.jspdf || (await import('jspdf'))
      const pdf = new jsPDF('p', 'mm', 'a4')

      const pageHeight = 297 // A4 height in mm
      const pageWidth = 210 // A4 width in mm
      const margin = 5

      const maxContentHeight = pageHeight - 2 * margin - 3
      const itemsPerPage = 6

      // Full header on every page; footer (totals/GST/declaration) never mid-cut —
      // if it won't fit with items, it goes on the next page with the same header.
      const buildTaxPage = (pageItems, startIdx, includeFooter) => `
  <div style="font-family: Arial; width: 100%; background: white; padding: 0; margin: 0; box-sizing: border-box;">
    ${generateHeader()}
          
          <!-- Items Table -->
          <div style="border:1px solid #000; border-top:none; padding:0; margin:0;">
            <table style="width:100%; border-collapse:collapse;">
              <thead>
                <tr>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Sl</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Description</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">UOM</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Part Code</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">HSN</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">GST%</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Qty</th>
                  <th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Rate</th>
                  ${hasDiscount
          ? `<th style="padding:8px; font-size:12px; text-align:center; border-right:1px solid #000; border-bottom:1px solid #000; background-color:white; color:black;">Disc%</th>`
          : ``
        }
                  <th style="padding:8px; font-size:12px; text-align:center; border-bottom:1px solid #000; background-color:white; color:black;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${pageItems
          .map((it, idx) => {
            const globalIdx = startIdx + idx
            const narration = pickItems[globalIdx]?.narration || ''
            const discount = pickItems[globalIdx]?.discount || 0
            const partCode = pickItems[globalIdx]?.model?.partCode || '-'

            const productName = narration
              ? `${String(it.PrdDesc || '-')} <br/> (${String(
                narration
              )})`
              : String(it.PrdDesc || '-')

            return `
                      <tr style="${idx === pageItems.length - 1 && !includeFooter ? "border-bottom:1px solid #000;" : ""}">
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${globalIdx + 1}</td>
                        <td style="padding:4px; font-size:12px; border-right:1px solid #000;">${productName}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${it.Unit === 'Project' ? 'Nos' : it.Unit === 'NA' ? ' ' : String(it.Unit || '-')}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(partCode || '').match(/.{1,15}/g)?.join('<br>') || ''}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(it.HsnCd || '-')}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(it.GstRt || 0)}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${it.Qty === 0 ? '' : Number(it.Qty)}</td>
                        <td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;">${money(
              it.UnitPrice
            )}</td>
                        ${hasDiscount
                ? `<td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;">${discount}</td>`
                : ``
              }
                        <td style="padding:4px; font-size:12px; text-align:right;">${money(
                it.AssAmt
              )}</td>
                      </tr>
                    `
          })
          .join('')}
                
                ${includeFooter
          ? `
                <tr>
                  <td colspan="${hasDiscount ? 9 : 8
          }" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">Total</td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(
            val?.AssVal
          )}</td>
                </tr>
               ${(() => {
            if (gstReq?.TranDtls?.SupTyp === 'SEZWOP') return '';
            const cs = hasDiscount ? 9 : 8;
            if (isIGST) {
              return '<tr><td colspan="' + cs + '" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">IGST</td><td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">' + money(val?.IgstVal || 0) + '</td></tr>';
            }
            return '<tr><td colspan="' + cs + '" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">CGST</td><td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">' + money(val?.CgstVal || inv?.cgstAmount || 0) + '</td></tr><tr><td colspan="' + cs + '" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">SGST</td><td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">' + money(val?.SgstVal || inv?.sgstAmount || 0) + '</td></tr>';
          })()}
                
                <tr>
                  <td colspan="${hasDiscount ? 9 : 8}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">
                    Net Total
                  </td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(
            Math.round(netTotal)
          )}</td>
                </tr>
                ${(() => {
            const diff = round;
            if (diff === 0) return '';
            return `
                <tr>
                  <td colspan="${hasDiscount ? 9 : 8}" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">
                    Rounded
                  </td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">
                    ${diff > 0 ? '+' : ''}${diff.toFixed(2)}
                  </td>
                </tr>
              `;
          })()}
                <tr style="border-top:1px solid #000;">
                  <td colspan="${hasDiscount ? 10 : 9}" style="padding:8px; font-size:11px; font-style:italic">
                    <b>${numberToWords(Math.round(netTotal))}</b>
                  </td>
                </tr>
                `
          : ''
        }
              </tbody>
            </table>
          </div>

          ${includeFooter
          ? `
          <!-- GST Breakup -->
          <div style="border:1px solid #000; border-top:none; padding:8px;">
            ${isIGST
            ? `
                <table border="1" style="width:100%; border-collapse:collapse">
                  <tr>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">HSN/SAC</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">Taxable Value</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">IGST Rate</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">IGST Amt</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">Total Tax Amt</th>
                  </tr>
                  ${hsnGroups
              .map(group => {

                return `
                    <tr>
                      <td style="padding:4px; font-size:12px">${String(
                  group.hsnCode
                )}</td>
                      <td style="padding:4px; font-size:12px">${money(
                  group.taxableValue
                )}</td>
                      <td style="padding:4px; font-size:12px">${Number(
                  group.gstRate || 0
                )}%</td>
                      <td style="padding:4px; font-size:12px">${money(
                  group.igstAmt
                )}</td>
                      <td style="padding:4px; font-size:12px">${money(
                  group.igstAmt
                )}</td>
                    </tr>
                  `
              })
              .join('')}
                  <tr style="font-weight:bold">
                    <td style="padding:4px; font-size:12px">Total</td>
                    <td style="padding:4px; font-size:12px">${money(
                hsnGroups.reduce((sum, g) => sum + g.taxableValue, 0)
              )}</td>
                    <td style="padding:4px; font-size:12px"></td>
                    <td style="padding:4px; font-size:12px">${money(
                hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0)
              )}</td>
                    <td style="padding:4px; font-size:12px">${money(
                hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0)
              )}</td>
                  </tr>
                  <tr>
                    <td colspan="5" style="padding:8px; font-size:11px; font-style:italic; background-color:#f9f9f9;">
                      <b>${numberToWords(
                hsnGroups.reduce((sum, g) => sum + g.igstAmt, 0)
              )}</b>
                    </td>
                  </tr>
                </table>
              `
            : `
                <table border="1" style="width:100%; border-collapse:collapse">
                  <tr>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">HSN/SAC</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">Taxable Value</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">CGST Rate</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">CGST Amt</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">SGST Rate</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">SGST Amt</th>
                    <th style="padding:4px; font-size:12px; background-color:white; color:black;">Total Tax Amt</th>
                  </tr>
                  ${hsnGroups
              .map(group => {
                const cgstRate = Number(group.gstRate || 0) / 2;
                const sgstRate = Number(group.gstRate || 0) / 2;
                return `
                    <tr>
                      <td style="padding:4px; font-size:12px">${String(
                  group.hsnCode
                )}</td>
                      <td style="padding:4px; font-size:12px">${money(
                  group.taxableValue
                )}</td>
                      <td style="padding:4px; font-size:12px">${cgstRate}%</td>
                      <td style="padding:4px; font-size:12px">${money(
                  group.cgstAmt
                )}</td>
                      <td style="padding:4px; font-size:12px">${sgstRate}%</td>
                      <td style="padding:4px; font-size:12px">${money(
                  group.sgstAmt
                )}</td>
                      <td style="padding:4px; font-size:12px">${money(
                  group.cgstAmt + group.sgstAmt
                )}</td>
                    </tr>
                  `
              })
              .join('')}
                    
                  <tr style="font-weight:bold">
                    <td style="padding:4px; font-size:12px">Total</td>
                    <td style="padding:4px; font-size:12px">${money(
                hsnGroups.reduce((sum, g) => sum + g.taxableValue, 0)
              )}</td>
                    <td style="padding:4px; font-size:12px"></td>
                    <td style="padding:4px; font-size:12px">${money(

                hsnGroups.reduce((sum, g) => sum + g.cgstAmt, 0)

              )}</td>
                    <td style="padding:4px; font-size:12px"></td>
                    <td style="padding:4px; font-size:12px">${money(

                hsnGroups.reduce((sum, g) => sum + g.sgstAmt, 0)

              )}</td>
                    <td style="padding:4px; font-size:12px">${money(

                hsnGroups.reduce(
                  (sum, g) => sum + g.cgstAmt + g.sgstAmt,
                  0
                )

              )}</td>
                  </tr>
                  <tr>
                    <td colspan="7" style="padding:8px; font-size:11px; font-style:italic; background-color:#f9f9f9;">
                      <b>${numberToWords(
                hsnGroups.reduce((sum, g) => sum + g.cgstAmt + g.sgstAmt, 0)
              )}</b>
                    </td>
                  </tr>
                  
                </table>
              `
          }
          </div>
          
          <!-- Declaration -->
          <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between; page-break-inside:avoid;">
            <div style="width:48%; border-right:1px solid #000; padding-right:10px;">
              <b style="font-size:12px">Declaration</b><br/>
              <span style="font-size:12px">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</span>
            </div>

            <div style="width:48%; padding-left:10px">
              <b style="font-size:12px">Company's Bank Details</b><br/>
              <span style="font-size:12px">Bank Name: <b>Union Bank of India</b></span><br/>
              <span style="font-size:12px">A/C No: <b>607605010000127</b></span><br/>
              <span style="font-size:12px">Branch & IFSC: <b>Bidhan Nagar Branch & UBIN0560766</b></span>
            </div>
          </div>

          <!-- Signatures -->
          <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between; align-items:flex-end; height:80px; page-break-inside:avoid;">
            <div style="font-size:12px">
             
              Customer Seal and Signature
            </div>

            <div style="text-align:right; font-size:12px">
              
              Authorised Signatory
            </div>
          </div>
          `
          : ''
        }
        </div>
      `

      const pages = []
      let idx = 0

      if (items.length === 0) {
        pages.push(buildTaxPage([], 0, true))
      } else {
        while (idx < items.length) {
          let take = Math.min(itemsPerPage, items.length - idx)
          const isLastChunk = idx + take >= items.length

          if (isLastChunk) {
            const withFooter = buildTaxPage(items.slice(idx, idx + take), idx, true)
            if (measurePageHeightMm(container, withFooter, pageWidth, margin) <= maxContentHeight) {
              pages.push(withFooter)
              break
            }
            // Keep sections whole: items on this page, full header + footer on next
            while (
              take > 1 &&
              measurePageHeightMm(
                container,
                buildTaxPage(items.slice(idx, idx + take), idx, false),
                pageWidth,
                margin
              ) > maxContentHeight
            ) {
              take -= 1
            }
            pages.push(buildTaxPage(items.slice(idx, idx + take), idx, false))
            idx += take
            if (idx >= items.length) {
              pages.push(buildTaxPage([], 0, true))
              break
            }
            continue
          }

          while (
            take > 1 &&
            measurePageHeightMm(
              container,
              buildTaxPage(items.slice(idx, idx + take), idx, false),
              pageWidth,
              margin
            ) > maxContentHeight
          ) {
            take -= 1
          }
          pages.push(buildTaxPage(items.slice(idx, idx + take), idx, false))
          idx += take
        }
      }

      for (let pageNum = 0; pageNum < pages.length; pageNum++) {
        if (pageNum > 0) pdf.addPage()
        await captureAndAddPdfPage(pdf, container, pages[pageNum], margin, pageWidth, pageHeight, qrImg)
      }

      // Save PDF
      pdf.save(`invoice-${inv?.invoiceNumber || 'INV'}.pdf`)
    } finally {
      // Clean up
      document.body.removeChild(container)
    }
  }

  const handleDownloadExportInvoice = async inv => {
    const gstReq = inv.gstzenRequestPayload || {}
    const seller = gstReq.SellerDtls || {}
    const buyer = gstReq.BuyerDtls || {}
    const ship = gstReq.ShipDtls || gstReq.DispDtls || {}
    const val = gstReq.ValDtls || {}
    const items = Array.isArray(gstReq.ItemList) ? gstReq.ItemList : []
    const totalAmount = items.reduce((sum, it, idx) => {
      const qty = Number(it.Qty || 0)
      const rate = Number(it.UnitPrice || 0)
      const baseAmount = qty * rate
      const discount = Number(it.Discount || 0)
      return sum + (baseAmount - discount)
    }, 0)
    const pickItems = inv.pickList?.lineItems || []

    const hasDiscount = pickItems.some(it => Number(it?.discount || 0) > 0)
    const isProforma = inv.isProformaInvoice === true
    const poNumber = inv.salesOrder?.customerPONumber || '-'
    const billingAdd = buyer?.Loc || '-'
    const billingPin = buyer?.Pin || '-';
    const shippingAdd = gstReq?.ShipDtls?.Addr1 || '-'
    const shippingPin = ship?.Pin || '-';
    const poDate = inv.salesOrder?.customerPODate || '-'
    const soNo = inv.salesOrder?.salesOrderNumber || '-'
    const priceBasis = inv.salesOrder?.priceBasis || '-'
    const irn = inv.irn || inv.gstzenResponse?.Irn || '-'
    const ackNo = inv.ackNo || inv.gstzenResponse?.AckNo || '-'
    const ackDate = inv.ackDate || inv.gstzenResponse?.AckDt || '-'
    const isIGST = Number(val?.IgstVal || inv?.igstAmount || 0) > 0

    // Export-specific fields
    const letterOfCreditNo = inv.letterOfCreditNo || '-'
    const letterOfCreditDate = inv.letterOfCreditDate
      ? new Date(inv.letterOfCreditDate).toLocaleDateString('en-IN')
      : '-'
    const amendmentNo = inv.amendmentNo || '-'
    const amendmentDate = inv.amendmentDate
      ? new Date(inv.amendmentDate).toLocaleDateString('en-IN')
      : '-'
    const insuranceCoverNoteNo = inv.insuranceCoverNoteNo || '-'
    const countryOfOrigin = inv.countryOfOrigin || '-'
    const countryOfDestination = inv.countryOfDestination || '-'
    const destinationCountryCode = inv.destinationCountryCode || '-'
    const portOfLoading = inv.portOfLoading || '-'
    const portOfDischarge = inv.portOfDischarge || '-'
    const termsOfDelivery = inv.termsOfDelivery || '-'
    const piNo = inv.piNo || '-'
    const piDate = inv.piDate
      ? new Date(inv.piDate).toLocaleDateString('en-IN')
      : '-'
    const termsOfPayment = inv.termsOfPayment || '-'
    const marking = inv.marking || '-'
    const noOfPackages = inv.noOfPackages || '-'
    const grossWeight = inv.grossWeight || '-'
    const netWeight = inv.netWeight || '-'
    const customerBankDetails = inv.customerBankDetails || ''
    const currencyType = inv.currencyType || 'INR'

    // convert number to ₹ format
    const money = n =>
      (Number(n) || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })

    // Convert number to words (USD for export invoices)
    const numberToWords = num => {

      const ones = [
        '',
        'One',
        'Two',
        'Three',
        'Four',
        'Five',
        'Six',
        'Seven',
        'Eight',
        'Nine'
      ]
      const teens = [
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen'
      ]
      const tens = [
        '',
        '',
        'Twenty',
        'Thirty',
        'Forty',
        'Fifty',
        'Sixty',
        'Seventy',
        'Eighty',
        'Ninety'
      ]

      const convertLessThanThousand = n => {
        if (n === 0) return ''
        if (n < 10) return ones[n]
        if (n < 20) return teens[n - 10]
        if (n < 100)
          return (
            tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
          )
        return (
          ones[Math.floor(n / 100)] +
          ' Hundred' +
          (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '')
        )
      }

      if (num === 0) return `${currencyType} Zero Only`

      // For export invoices, use international format (Million, Billion) instead of Indian format (Lakh, Crore)
      const billion = Math.floor(num / 1000000000)
      const million = Math.floor((num % 1000000000) / 1000000)
      const thousand = Math.floor((num % 1000000) / 1000)
      const hundred = num % 1000

      let result = ''

      if (billion > 0) result += convertLessThanThousand(billion) + ' Billion '
      if (million > 0) result += convertLessThanThousand(million) + ' Million '
      if (thousand > 0)
        result += convertLessThanThousand(thousand) + ' Thousand '
      if (hundred > 0) result += convertLessThanThousand(hundred)

      return `${currencyType} ` + result.trim() + ' Only'
    }

    // Generate QR Code Image — large + quiet zone so scanners can read dense e-invoice payload
    let qrImg = ''
    if (inv.gstzenResponse?.SignedQRCode) {
      qrImg = await QRCode.toDataURL(String(inv.gstzenResponse.SignedQRCode).trim(), {
        errorCorrectionLevel: 'L',
        type: 'image/png',
        margin: 4,
        width: 400,
        color: { dark: '#000000', light: '#FFFFFF' }
      })
    }

    // Group items by HSN code for GST breakup
    const groupedByHsn = items.reduce((acc, it) => {
      const hsn = it.HsnCd ? String(it.HsnCd).trim() : '-'
      if (!acc[hsn]) {
        acc[hsn] = {
          hsnCode: hsn,
          taxableValue: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          igstAmt: 0,
          gstRate: it.GstRt || 0
        }
      }
      const itemTotal = Number(it.Qty || 0) * Number(it.UnitPrice || 0)
      acc[hsn].taxableValue += itemTotal
      acc[hsn].cgstAmt += Number(it.CgstAmt || 0)
      acc[hsn].sgstAmt += Number(it.SgstAmt || 0)
      acc[hsn].igstAmt += Number(it.IgstAmt || 0)
      return acc
    }, {})

    const hsnGroups = Object.values(groupedByHsn)

    // Calculate net total with tax
    const netTotal = Math.round(
      (val?.SgstVal || inv?.sgstAmount || 0) +
      (val?.CgstVal || inv?.cgstAmount || 0) +
      (val?.IgstVal || inv?.igstAmount || 0) +
      totalAmount
    )

    // Helper function to generate header HTML for export invoice
    const generateExportHeader = () => `
    <div style="border:1px solid #000; background: white;">
      <!-- Header -->
      <div style="text-align:center; padding:8px 20px;">
        <h2 style="margin:5px 0;">Export Invoice</h2>
        <p style="margin:5px 0; font-size:12px;">Supply Meant for Export Under Letter of Undertaking Without Payment of Integrated Tax (IGST)</p>
      </div>
      ${isProforma
        ? ''
        : `
      <div style="display:flex; justify-content:space-between; align-items:center; padding:8px 10px; border-top:1px solid #000; border-bottom:1px solid #000">
        <div style="padding:4px 8px; flex:1; min-width:0; font-size:9px; line-height:1.35;">
          <div style="white-space:nowrap;"><b>IRN:</b> ${String(irn || '-')}</div>
          <div><b>Ack No:</b> ${String(ackNo || '-')}</div>
          <div><b>Ack Date:</b> ${String(ackDate || '-')}</div>
        </div>  

        <div style="flex-shrink:0; padding:4px; text-align:center;">
          <div style="font-size:11px; margin-bottom:2px;">e-Invoice</div>
          ${qrImg
          ? `<img data-einvoice-qr="1" src="${qrImg}" width="180" height="180" style="width:180px;height:180px;image-rendering:pixelated;display:block;border:0;padding:0;margin:0;" />`
          : `<div style="width:180px;height:180px;display:flex;align-items:center;justify-content:center;font-size:10px">QR</div>`
        }
        </div>
      </div>
    `
      } 
          
      <!-- Seller / Invoice Details -->
      <div style="display:flex; border-bottom:1px solid #000; ${isProforma ? 'border-top:1px solid #000;' : ''
      }">
        <div style="width:40%; padding:8px; border-right:1px solid #000">
          <div style="font-size:12px; font-weight:bold">${String(
        seller.LglNm || '-'
      )}</div>
          <div style="font-size:12px">${String(seller.Addr1 || '')}</div>
          <div style="font-size:12px">GSTIN: ${String(
        seller.Gstin || '-'
      )}</div>
          <div style="font-size:12px">CIN: U74140WB2011PTC159722</div>
          <div style="font-size:12px">UDYAM Registration no: UDYAM-WB-09-0000238</div>
        </div>

        <div style="width:60%; padding:8px">
          <table style="width:100%; border-collapse:collapse">
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Invoice No</td>
            <td style="padding:2px 4px; font-size:12px">${String(
        gstReq?.DocDtls?.No || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Invoice Date</td><td style="padding:2px 4px; font-size:12px">${String(
        gstReq?.DocDtls?.Dt || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Customer PO No.</td><td style="padding:2px 4px; font-size:12px">${String(
        poNumber || '-'
      )}</td><tr>
  <td style="font-weight:bold; padding:2px 4px; font-size:12px">
    Customer PO Date
  </td>
            <td style="padding:2px 4px; font-size:12px">
              ${poDate
        ? new Date(poDate).toLocaleDateString('en-GB')
        : '-'
      }
            </td>
            </tr>

            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">SO Id</td><td style="padding:2px 4px; font-size:12px">${String(
        soNo || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Price Basis</td><td style="padding:2px 4px; font-size:12px">${String(
        priceBasis || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Dispatch Doc No.</td><td style="padding:2px 4px; font-size:12px">${String(
        inv.dispatchDocumentNumber || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Dispatched Through</td><td style="padding:2px 4px; font-size:12px">${String(
        inv.dispatchedThrough || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Vehicle Details</td><td style="padding:2px 4px; font-size:12px">${String(
        inv.vehicleNumber || '-'
      )}</td></tr>
            <tr><td style="font-weight:bold; padding:2px 4px; font-size:12px">Other References</td><td style="padding:2px 4px; font-size:12px">${String(
        inv.otherReferences || '-'
      )}</td></tr>
          </table>
        </div>
      </div>

      <!-- Export Specific Details -->
      <div style="border-bottom:1px solid #000; padding:8px">
        <table style="width:100%; border-collapse:collapse">
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px; width:25%">Letter of Credit No.</td>
            <td style="padding:2px 4px; font-size:12px; width:25%">${letterOfCreditNo}</td>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px; width:25%">LC Date</td>
            <td style="padding:2px 4px; font-size:12px; width:25%">${letterOfCreditDate}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Amendment No.</td>
            <td style="padding:2px 4px; font-size:12px">${amendmentNo}</td>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Amendment Date</td>
            <td style="padding:2px 4px; font-size:12px">${amendmentDate}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Insurance Cover Note No.</td>
            <td style="padding:2px 4px; font-size:12px" colspan="3">${insuranceCoverNoteNo}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Country of Origin</td>
            <td style="padding:2px 4px; font-size:12px">${countryOfOrigin}</td>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Country of Destination</td>
            <td style="padding:2px 4px; font-size:12px">${countryOfDestination} (${destinationCountryCode})</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Port of Loading</td>
            <td style="padding:2px 4px; font-size:12px">${portOfLoading}</td>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Port of Discharge</td>
            <td style="padding:2px 4px; font-size:12px">${portOfDischarge}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Terms of Delivery</td>
            <td style="padding:2px 4px; font-size:12px">${String(termsOfDelivery).toUpperCase()}</td>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Terms of Payment</td>
            <td style="padding:2px 4px; font-size:12px">${String(termsOfPayment).toUpperCase()}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">PI No.</td>
            <td style="padding:2px 4px; font-size:12px">${piNo}</td>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">PI Date</td>
            <td style="padding:2px 4px; font-size:12px">${piDate}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Marking</td>
            <td style="padding:2px 4px; font-size:12px">${marking}</td>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">No. of Packages</td>
            <td style="padding:2px 4px; font-size:12px">${noOfPackages}</td>
          </tr>
          <tr>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Gross Weight (Kg)</td>
            <td style="padding:2px 4px; font-size:12px">${grossWeight}</td>
            <td style="font-weight:bold; padding:2px 4px; font-size:12px">Net Weight (Kg)</td>
            <td style="padding:2px 4px; font-size:12px">${netWeight}</td>
          </tr>
        </table>
      </div>

      <!-- Consignee / Buyer -->
      <div style="display:flex; border-bottom:1px solid #000">
        
        <div style="width:50%; padding:8px">
          <div style="font-size:12px; font-weight:bold">Bill To</div>
          <div style="font-size:12px">${String(buyer.LglNm || '-')}</div>
          <div style="font-size:12px">${String(billingAdd || '-')}</div>
          <div style="font-size:12px">Pin - ${String(billingPin || '-')}</div>
        </div>
        <div style="width:50%; padding:8px; border-left:1px solid #000">
          <div style="font-size:12px; font-weight:bold">Consignee (Ship To)</div>
          <div style="font-size:12px">${String(ship.LglNm || '-')}</div>
          <div style="font-size:12px">${String(shippingAdd || '-')}</div>
          <div style="font-size:12px">Pin - ${String(shippingPin || '-')}</div>
        </div>

      </div>
    </div>
  `

    // Create a temporary container for rendering
    const container = document.createElement('div')
    document.body.appendChild(container)

    try {
      // Import jsPDF dynamically if not already available
      const { jsPDF } = window.jspdf || (await import('jspdf'))
      const pdf = new jsPDF('p', 'mm', 'a4')

      const pageHeight = 297 // A4 height in mm
      const pageWidth = 210 // A4 width in mm
      const margin = 5

      const maxContentHeight = pageHeight - 2 * margin - 3
      const itemsPerPage = 4

      const buildExportPage = (pageItems, startIdx, includeFooter) => `
        <div style="font-family: Arial; width: 100%; background: white; padding: 0; margin: 0;">
          ${generateExportHeader()}
          
          <!-- Items Table -->
          <div style="border:1px solid #000; border-top:none; padding:0; margin:0;">
            <table style="width:100%; border-collapse:collapse;">
              <thead>
                <tr style="border-bottom:1px solid #000;">
                  <th style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000; background-color:white; color:black;">Sl</th>
                  <th style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000; background-color:white; color:black;">Description</th>
                  <th style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000; background-color:white; color:black;">Part Code</th>
                  <th style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000; background-color:white; color:black;">UOM</th>
                  <th style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000; background-color:white; color:black;">HSN</th>
                  <th style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000; background-color:white; color:black;">Qty</th>
                  <th style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000; background-color:white; color:black;">Rate</th>
                  ${hasDiscount
          ? `<th style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000; background-color:white; color:black;">Disc%</th>`
          : ``
        }
                  <th style="padding:4px; font-size:12px; text-align:center; background-color:white; color:black;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${pageItems
          .map((it, idx) => {
            const globalIdx = startIdx + idx
            const narration = pickItems[globalIdx]?.narration || ''
            const discount = pickItems[globalIdx]?.discount || 0
            const partCode = pickItems[globalIdx]?.model?.partCode || '-'

            const productName = narration
              ? `${String(it.PrdDesc || '-')} <br/> (${String(
                narration
              )})`
              : String(it.PrdDesc || '-')

            const amount =
              (Number(it.UnitPrice || 0) * Number(it.Qty || 0)) - (Number(it.Discount || 0))

            return `
                      <tr style="${idx === pageItems.length - 1 && !includeFooter ? "border-bottom:1px solid #000;" : ""}">
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${globalIdx + 1
              }</td>
                        <td style="padding:4px; font-size:12px; border-right:1px solid #000;">${productName}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${partCode}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(
                it.Unit || '-'
              )}</td>
                        <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${String(
                it.HsnCd || '-'
              )}</td>
                <td style="padding:4px; font-size:12px; text-align:center; border-right:1px solid #000;">${Number(
                it.Qty || 0
              )}</td>
                        <td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;">${money(
                it.UnitPrice
              )}</td>
                        ${hasDiscount
                ? `<td style="padding:4px; font-size:12px; text-align:right; border-right:1px solid #000;">${Number(
                  discount
                )}</td>`
                : ``
              }
                        <td style="padding:4px; font-size:12px; text-align:right;">${money(
                amount
              )}</td>
                      </tr>
                    `
          })
          .join('')}
                
                ${includeFooter
          ? `
                <tr>
                  <td colspan="${hasDiscount ? 8 : 7
          }" style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">Total</td>
                  <td style="padding:4px; font-size:12px; text-align:right; font-weight:bold;">${money(
            Math.round(totalAmount)
          )}</td>
                </tr>
                
                <tr>
                  <td colspan="${hasDiscount ? 9 : 8
          }" style="padding:8px; font-size:11px; font-style:italic; background-color:#f9f9f9;">
                    <b>${numberToWords(Math.round(totalAmount))}</b>
                  </td>
                </tr>
                `
          : ''
        }
              </tbody>
            </table>
          </div>

          ${includeFooter
          ? `
          
          <!-- Declaration -->
          <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between; page-break-inside:avoid;">
            <div style="width:48%; border-right:1px solid #000; padding-right:10px;">
              <b style="font-size:12px">Declaration</b><br/>
              <span style="font-size:12px">This is Certify that, the description and value of the goods covered under our Invoice have been checked by me and loaded on the Vehicle under my supervision. The goods have been checked and sealed with seal no. MBIPL under my supervision. The Export is made under self sealing procedure vide Sec. 54 of CGST Act, 2017.</span>
            </div>

            <div style="width:48%; padding-left:10px">
              <b style="font-size:12px">Customer Bank Details</b><br/>
              <span style="font-size:12px; white-space: pre-line;">${customerBankDetails || 'N/A'}</span>
            </div>
            <div style="width:48%; padding-left:10px">
              <b style="font-size:12px">Our Bank Details</b><br/>
              <span style="font-size:12px">Bank Name: <b>Union Bank of India</b></span><br/>
              <span style="font-size:12px">A/C No: <b>607605010000127</b></span><br/>
              <span style="font-size:12px">Branch & IFSC: <b>Kolkata - Overseas & UBIN0549568</b></span>
              <span style="font-size:12px">Swift Code: <b>UBININBBOCL</b></span>
            </div>
          </div>

          <!-- Signatures -->
          <div style="border:1px solid #000; border-top:none; padding:8px; display:flex; justify-content:space-between; align-items:flex-end; height:80px; page-break-inside:avoid;">
            <div style="font-size:12px">
            
              Customer Seal and Signature
            </div>

            <div style="text-align:right; font-size:12px">
              
              Authorised Signatory
            </div>
          </div>
          `
          : ''
        }
        </div>
      `

      const pages = []
      let idx = 0

      if (items.length === 0) {
        pages.push(buildExportPage([], 0, true))
      } else {
        while (idx < items.length) {
          let take = Math.min(itemsPerPage, items.length - idx)
          const isLastChunk = idx + take >= items.length

          if (isLastChunk) {
            const withFooter = buildExportPage(items.slice(idx, idx + take), idx, true)
            if (measurePageHeightMm(container, withFooter, pageWidth, margin) <= maxContentHeight) {
              pages.push(withFooter)
              break
            }
            while (
              take > 1 &&
              measurePageHeightMm(
                container,
                buildExportPage(items.slice(idx, idx + take), idx, false),
                pageWidth,
                margin
              ) > maxContentHeight
            ) {
              take -= 1
            }
            pages.push(buildExportPage(items.slice(idx, idx + take), idx, false))
            idx += take
            if (idx >= items.length) {
              pages.push(buildExportPage([], 0, true))
              break
            }
            continue
          }

          while (
            take > 1 &&
            measurePageHeightMm(
              container,
              buildExportPage(items.slice(idx, idx + take), idx, false),
              pageWidth,
              margin
            ) > maxContentHeight
          ) {
            take -= 1
          }
          pages.push(buildExportPage(items.slice(idx, idx + take), idx, false))
          idx += take
        }
      }

      for (let pageNum = 0; pageNum < pages.length; pageNum++) {
        if (pageNum > 0) pdf.addPage()
        await captureAndAddPdfPage(pdf, container, pages[pageNum], margin, pageWidth, pageHeight, qrImg)
      }

      // Save PDF
      pdf.save(`export-invoice-${inv.invoiceNumber || inv.proformaInvoiceNumber || 'INV'}.pdf`)
    } finally {
      // Clean up
      document.body.removeChild(container)
    }
  }

  const toggleAccordion = id => {
    setOpenId(prev => (prev === id ? '' : id))
  }

  const fetchInvoices = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({ pagination: 'false' })
      if (storeid) params.set('storeId', storeid)
      if (debouncedSearch) params.set('search', debouncedSearch)  // ← added

      const url = `${API_URL}invoices?${params}`

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        redirect: 'follow'
      })

      if (!res.ok) {
        const txt = await res.text()
        throw new Error(txt || `Status ${res.status}`)
      }

      const json = await res.json()
      const docs =
        json?.message?.docs ||
        json?.data?.docs ||
        json?.docs ||
        (Array.isArray(json) ? json : [])

      setInvoices(docs)
    } catch (err) {
      console.error('Error fetching invoices:', err)
      setError(err.message || 'Failed to fetch invoices')
      setInvoices([])
    } finally {
      setLoading(false)
    }
  }, [token, storeid, debouncedSearch])  // ← added debouncedSearch

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  // compute grand total of all invoices
  const summaryTotals = useMemo(() => {
    const total = invoices.reduce(
      (acc, inv) => acc + (Number(inv.totalInvoiceValue) || 0),
      0
    )
    return { total }
  }, [invoices])

  const renderItemLines = invoice => {
    const items =
      invoice?.gstzenRequestPayload?.ItemList &&
        Array.isArray(invoice.gstzenRequestPayload.ItemList)
        ? invoice.gstzenRequestPayload.ItemList
        : []

    if (items.length === 0) {
      return (
        <tr>
          <td colSpan='11' className='text-center py-3'>
            No item details available
          </td>
        </tr>
      )
    }

    return items.map((it, idx) => (
      <tr key={idx}>
        <td>{it.SlNo ?? idx + 1}</td>
        <td>{it.PrdDesc ?? '-'}</td>
        <td>{it.HsnCd ?? '-'}</td>
        <td>{it.Unit ?? '-'}</td>
        <td>{it.Qty ?? 0}</td>
        <td>{money(it.UnitPrice)}</td>
        <td>{money(it.TotAmt)}</td>
        <td>{money(it.Discount)}</td>
        <td>{`${Number(it.GstRt || 0).toFixed(2)}%`}</td>
        <td>{money(it.AssAmt)}</td>
        <td>{money(it.TotItemVal)}</td>
      </tr>
    ))
  }

  const openCancelModal = invoice => {
    const id = invoice._id || invoice.id
    if (!id) return
    setCancelInvoiceId(id)
    setCancelReason('')
    setCancelError(null)
    setCancelModalOpen(true)
  }

  const closeCancelModal = () => {
    if (cancelLoading) return
    setCancelModalOpen(false)
    setCancelInvoiceId(null)
    setCancelReason('')
    setCancelError(null)
  }

  const handleConfirmCancel = async () => {
    if (!cancelInvoiceId) {
      setCancelError('Unable to identify invoice to cancel.')
      return
    }

    if (!cancelReason.trim()) {
      setCancelError('Please enter a reason for cancellation.')
      return
    }

    try {
      setCancelLoading(true)
      setCancelError(null)

      const url = `${API_URL}invoices/${cancelInvoiceId}/cancel`

      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: cancelReason.trim()
        }),
        redirect: 'follow'
      })

      if (!res.ok) {
        const txt = await res.text()
        throw new Error(txt || `Status ${res.status}`)
      }

      // Optionally parse result if needed
      // const result = await res.json()

      // Refresh invoice list after successful cancellation
      await fetchInvoices()

      setCancelLoading(false)
      closeCancelModal()
    } catch (err) {
      console.error('Error cancelling invoice:', err)
      setCancelLoading(false)
      setCancelError(err.message || 'Failed to cancel invoice')
    }
  }

  const openConvertModal = invoice => {
    const id = invoice._id || invoice.id
    if (!id) return
    setConvertInvoiceId(id)
    // Pre-populate from the invoice record — no user input needed
    setConvertInvoiceNumber(invoice.invoiceNumber || '')
    setConvertError(null)
    setConvertModalOpen(true)
  }

  const closeConvertModal = () => {
    if (convertLoading) return
    setConvertModalOpen(false)
    setConvertInvoiceId(null)
    setConvertInvoiceNumber('')
    setConvertError(null)
  }

  const handleConfirmConvert = async () => {
    if (!convertInvoiceId) {
      setConvertError('Unable to identify invoice to convert.')
      return
    }

    try {
      setConvertLoading(true)
      setConvertError(null)

      // Find the invoice to convert
      const invoice = invoices.find(
        inv => String(inv._id || inv.id) === String(convertInvoiceId)
      )

      if (!invoice) {
        throw new Error('Invoice not found')
      }

      // Build payload similar to Invoice.js createInvoice function
      const pickList = invoice.pickList || {}
      const gstReq = invoice.gstzenRequestPayload || {}
      const buyer = gstReq.BuyerDtls || {}
      const ship = gstReq.ShipDtls || {}

      const payload = {
        invoiceId: invoice._id || invoice.id, // ← Invoice ID for conversion
        pickListId: pickList._id || pickList.id,
        supplyType: gstReq.TranDtls?.SupTyp || 'B2B',
        buyerGstin: buyer.Gstin || '',
        buyerPos: buyer.Pos || '',
        buyerLocation: buyer.Loc || '',
        buyerPin: buyer.Pin ? Number(buyer.Pin) : undefined,
        shipToGstin: ship.Gstin || '',
        shipToLegalName: ship.LglNm || '',
        shipToAddress: ship.Addr1 || '',
        shipToLocation: ship.Loc || '',
        shipToPin: ship.Pin ? Number(ship.Pin) : undefined,
        shipToStateCode: ship.Stcd || '',
        otherReferences: invoice.otherReferences || '',
        dispatchedThrough: invoice.dispatchedThrough || '',
        dispatchDocumentNumber: invoice.dispatchDocumentNumber || '',
        vehicleNumber: invoice.vehicleNumber || '',
        isProformaInvoice: false, // Converting to e-invoice
        invoiceNumber: convertInvoiceNumber.trim(),
        proformaInvoiceNumber: null
      }

      const url = `${API_URL}invoices/create`

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        redirect: 'follow'
      })

      if (!res.ok) {
        const txt = await res.text()
        let errMsg = `Status ${res.status}`
        try {
          const errJson = JSON.parse(txt)
          errMsg = errJson.message || errJson.error || txt
        } catch {
          errMsg = txt || errMsg
        }
        throw new Error(errMsg)
      }

      // Refresh invoice list after successful conversion
      await fetchInvoices()

      setConvertLoading(false)
      closeConvertModal()
      alert('Invoice successfully converted to E-Invoice!')
    } catch (err) {
      console.error('Error converting invoice:', err)
      setConvertLoading(false)
      setConvertError(err.message || 'Failed to convert invoice')
    }
  }

  /**
   * Open E-way Bill modal for a specific invoice
   * @param {Object} invoice - Invoice object
   */
  const openEwayBillModal = invoice => {
    if (!invoice) return
    setSelectedInvoiceForEwayBill(invoice)
    setEwayBillModalOpen(true)
  }

  /**
   * Close E-way Bill modal and reset state
   */
  const closeEwayBillModal = () => {
    setEwayBillModalOpen(false)
    setSelectedInvoiceForEwayBill(null)
  }

  /**
   * Handle successful E-way Bill generation
   * @param {Object} result - API response result
   */
  const handleEwayBillSuccess = async result => {
    console.log('E-way Bill generated successfully:', result)
    // Refresh invoices to get updated data
    await fetchInvoices()
  }

  // ---- Credit Note handlers ----
  // Prefer invoice TotAmt/AssAmt from gstzen payload — do NOT derive from UnitPrice * Qty
  // (service invoices often have Qty=0 while TotAmt holds the real line value).
  const getInvoiceLineTotAmt = it => Number(it?.TotAmt ?? it?.AssAmt ?? 0)

  const openCreditNoteModal = inv => {
    setCrnInvoice(inv)
    setCrnError(null)
    setCrnSuccess(null)
    // Initialise line-item selections: all selected with full qty / invoice TotAmt
    const items = Array.isArray(inv?.gstzenRequestPayload?.ItemList)
      ? inv.gstzenRequestPayload.ItemList
      : []
    const initItems = {}
    items.forEach((it, idx) => {
      const key = it.SlNo != null ? String(it.SlNo) : String(idx)
      const maxQty = Number(it.Qty || 0)
      const defaultAmount = +getInvoiceLineTotAmt(it).toFixed(2)
      // When invoice qty is 0 (value-only lines), default to zero-qty credit mode
      const zeroQty = maxQty === 0
      initItems[key] = {
        selected: true,
        qty: maxQty,
        maxQty,
        amount: defaultAmount,
        zeroQty,
        item: it
      }
    })
    setCrnLineItems(initItems)
    setCrnModalOpen(true)
  }

  const closeCreditNoteModal = () => {
    if (crnLoading) return
    setCrnModalOpen(false)
    setCrnInvoice(null)
    setCrnError(null)
    setCrnSuccess(null)
    setCrnLineItems({})
  }

  const handleConfirmCreateCreditNote = async () => {
    if (!crnInvoice) return

    // Validate: at least one item selected
    const selectedKeys = Object.keys(crnLineItems).filter(k => crnLineItems[k].selected)
    if (selectedKeys.length === 0) {
      setCrnError('Please select at least one line item for the credit note.')
      return
    }

    try {
      setCrnLoading(true)
      setCrnError(null)
      setCrnSuccess(null)

      const gstReq = crnInvoice.gstzenRequestPayload || {}
      const buyer = gstReq.BuyerDtls || {}
      const ship = gstReq.ShipDtls || {}

      // Build selected line items array with overridden quantities / amounts
      const selectedLineItems = selectedKeys.map(k => {
        const { item, qty, amount, zeroQty } = crnLineItems[k]
        const effectiveQty = qty
        // Use edited amount, else invoice TotAmt/AssAmt — never UnitPrice * Qty
        const effectiveAmt =
          amount != null && amount !== ''
            ? Number(amount)
            : +getInvoiceLineTotAmt(item).toFixed(2)
        return {
          ...item,
          // Always send the real quantity — backend uses it for calculations.
          // When markedZeroQty is true the backend will set GSTZen Qty = 0
          // but compute financials using this real Qty value.
          Qty: effectiveQty,
          markedZeroQty: !!zeroQty,
          // pass overridden totals so the backend can use them directly
          TotAmt: effectiveAmt,
          AssAmt: effectiveAmt
        }
      })

      const payload = {
        pickListId: crnInvoice.pickList?._id || crnInvoice.pickList?.id || crnInvoice.pickList,
        originalInvoiceId: crnInvoice._id || crnInvoice.id,
        supplyType: gstReq.TranDtls?.SupTyp || 'B2B',
        buyerGstin: buyer.Gstin || '',
        buyerPos: buyer.Pos || buyer.Stcd || '',
        buyerLocation: buyer.Loc || '',
        buyerPin: buyer.Pin ? Number(buyer.Pin) : undefined,
        shipToGstin: ship.Gstin || buyer.Gstin || '',
        shipToLegalName: ship.LglNm || buyer.LglNm || '',
        shipToAddress: ship.Addr1 || '',
        shipToLocation: ship.Loc || buyer.Loc || '',
        shipToPin: ship.Pin ? Number(ship.Pin) : undefined,
        shipToStateCode: ship.Stcd || '',
        otherReferences: crnInvoice.otherReferences || '',
        dispatchedThrough: crnInvoice.dispatchedThrough || '',
        dispatchDocumentNumber: crnInvoice.dispatchDocumentNumber || '',
        vehicleNumber: crnInvoice.vehicleNumber || '',
        selectedLineItems
      }

      const res = await fetch(`${API_URL}credit-notes/create`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      const json = await res.json()
      if (!res.ok) {
        throw new Error(json?.message || json?.error || `Status ${res.status}`)
      }

      const crnNumber = json?.data?.creditNote?.creditNoteNumber ||
        json?.message?.creditNote?.creditNoteNumber ||
        'CRN'
      setCrnSuccess(`Credit Note ${crnNumber} created successfully! You can now view and convert it to e-invoice from the View CRN tab.`)
      setCrnLoading(false)
    } catch (err) {
      console.error('Error creating credit note:', err)
      setCrnLoading(false)
      setCrnError(err.message || 'Failed to create credit note')
    }
  }

  const handleSyncTally = async (inv) => {
    const invoiceNumber = inv?.gstzenRequestPayload?.DocDtls?.No
    if (!invoiceNumber) {
      alert('Invoice number not found. Cannot sync to Tally.')
      return
    }
    const invId = String(inv._id || inv.id)
    setSyncTallyState(prev => ({
      ...prev,
      [invId]: { loading: true, error: null, success: null }
    }))
    try {
      const res = await fetch(`${API_URL}sales-invoices/sync`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invoiceNumber })
      })
      const text = await res.text()
      let message = text
      try { message = JSON.parse(text)?.message || text } catch (_) { }
      if (!res.ok) throw new Error(message || `Status ${res.status}`)
      setSyncTallyState(prev => ({
        ...prev,
        [invId]: { loading: false, error: null, success: message || 'Synced to Tally successfully!' }
      }))
    } catch (err) {
      console.error('Sync Tally error:', err)
      setSyncTallyState(prev => ({
        ...prev,
        [invId]: { loading: false, error: err.message || 'Sync failed', success: null }
      }))
    }
  }

  if (loading) {
    return (
      <div className='card shadow-sm'>
        <CardBody className='p-4 text-center'>
          <Spinner animation='border' size='sm' /> Loading invoices...
        </CardBody>
      </div>
    )
  }

  if (error) {
    return (
      <div className='card shadow-sm'>
        <CardBody className='p-4'>
          <div className='alert alert-danger mb-0'>Error: {error}</div>
        </CardBody>
      </div>
    )
  }

  if (!invoices || (invoices.length === 0 && !debouncedSearch && !loading)) {
    return (
      <div className='card shadow-sm'>
        <CardBody className='p-4 text-center'>No invoices found.</CardBody>
      </div>
    )
  }

  return (
    <div className='card shadow-sm'>
      <CardBody className='p-4'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h5 className='mb-0 ml-1'>Invoices</h5>
          <div className='text-muted small'>
            Total Invoice Value: <strong>₹ {money(summaryTotals.total)}</strong>
          </div>
        </div>
        <div className='d-flex flex-column flex-sm-row gap-2 mb-3 align-items-sm-center'>
          <div className='position-relative flex-grow-1'>
            <input
              type='text'
              className='form-control form-control-sm ps-4'
              placeholder='Search by invoice no., sales order no., company name…'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                className='btn btn-link btn-sm position-absolute p-0 text-muted'
                style={{ right: 8, top: '50%', transform: 'translateY(-50%)' }}
                onClick={() => setSearch('')}
              >✕</button>
            )}
          </div>
        </div>

        {paginated.length === 0 ? (
          <div className='text-center text-muted py-5'>
            {debouncedSearch ? `No invoices match "${debouncedSearch}"` : 'No invoices found.'}
          </div>
        ) : (
          <Accordion open={openId || ''} toggle={toggleAccordion} id='viewInvoiceAccordion'>
            {paginated.map(inv => {
              const id = String(inv._id || inv.id)

              const pickList = inv.pickList || {}
              const salesOrder = inv.salesOrder || {}
              const di = pickList.deliveryInstruction || {}
              const gstzenReq = inv.gstzenRequestPayload || {}
              const buyer = gstzenReq.BuyerDtls || {}
              const shipping = gstzenReq.ShipDtls || {}
              const seller = gstzenReq.SellerDtls || {}
              const val = gstzenReq.ValDtls || {}
              const so = inv.salesOrder || {}

              const invoiceNo = gstzenReq?.DocDtls?.No || '-'
              const invoiceDate =
                inv.invoiceDate || gstzenReq?.DocDtls?.Dt || null

              const status = inv.status || gstzenReq?.IrnStatus || '-'
              const irnStatus = inv.gstzenResponse?.IrnStatus

              const totalValue =
                Number(inv.totalInvoiceValue) || Number(val.TotInvVal) || 0
              const rounded = Number(val?.RndOffAmt)

              const taxable = Number(inv.taxableValue) || Number(val.AssVal) || 0

              const igst = Number(inv.igstAmount) || Number(val.IgstVal) || 0
              const cgst = Number(inv.cgstAmount) || Number(val.CgstVal) || 0
              const sgst = Number(inv.sgstAmount) || Number(val.SgstVal) || 0

              const hasError =
                !!inv.errorMessage || !!inv.gstzenResponse?.ErrorDetails?.length

              return (
                <AccordionItem key={id}>
                  <AccordionHeader targetId={id}>
                    <div className='d-flex flex-column flex-md-row align-items-start gap-2 w-100'>
                      <div className='me-auto'>
                        <strong></strong> {invoiceNo}{' '}
                        <span className='text-muted'>
                          | SO:{' '}
                          {salesOrder.salesOrderNumber ||
                            di.salesOrderNumber ||
                            '-'}
                        </span>{' '}
                        <span className='text-muted'>
                          | {buyer.LglNm || '-'}
                        </span>{' '}
                        <span className='text-muted'>
                          | Box: {pickList.boxNumber || '-'}
                        </span>
                      </div>

                      <div className='text-muted small'>
                        Date: {formatDateTime(invoiceDate)}
                      </div>

                      <div className='ms-md-3 d-flex gap-2 flex-wrap align-items-center'>

                        <span
                          className={`badge ${status === 'active'
                            ? 'bg-success'
                            : status === 'deleted'
                              ? 'bg-danger'
                              : 'bg-secondary'
                            }`}
                        >
                          Status: {status}
                        </span>

                        {irnStatus && (
                          <span
                            className={`badge ${irnStatus === 'GEN'
                              ? 'bg-success'
                              : irnStatus === 'ERR'
                                ? 'bg-danger'
                                : 'bg-secondary'
                              }`}
                          >
                            IRN: {irnStatus}
                          </span>
                        )}

                        <span className='badge bg-light text-dark'>
                          Total: ₹ {money(totalValue)}
                        </span>

                        {/* Cancel button */}
                      </div>
                    </div>
                  </AccordionHeader>

                  <AccordionBody accordionId={id}>
                    {/* Top summary (Buyer / Seller) */}
                    <div className='row mb-3'>
                      <div className='col-md-6'>
                        <h6>Seller Details</h6>
                        <div className='small'>
                          <div>
                            <strong>Name:</strong> {seller.LglNm || '-'}
                          </div>
                          <div>
                            <strong>GSTIN:</strong> {seller.Gstin || '-'}
                          </div>
                          <div>
                            <strong>Address:</strong> {seller.Addr1 || '-'}
                          </div>
                          <div>
                            <strong>Location:</strong> {seller.Loc || '-'} (
                            {seller.Stcd || '-'}) - {seller.Pin || '-'}
                          </div>
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <h6>Buyer Details</h6>
                        <div className='small'>
                          <div>
                            <strong>Name:</strong> {buyer.LglNm || '-'}
                          </div>
                          <div>
                            <strong>GSTIN:</strong> {buyer.Gstin || '-'}
                          </div>
                          <div>
                            <strong>Billing Address:</strong> {buyer.Loc || '-'}
                          </div>
                          <div>
                            <strong>Pin:</strong> {buyer.Pin || '-'}
                          </div>
                          <div>
                            <strong>Shipping Address:</strong> {shipping.Addr1 || '-'}
                          </div>
                          <div>
                            <strong>Pin:</strong> {shipping.Pin || '-'}
                          </div>
                          <div>
                            <strong>Location:</strong> {shipping.Loc || '-'} (
                            {shipping.Stcd || '-'})
                          </div>
                          <div>
                            <strong>Pin:</strong> {shipping.Pin || '-'}
                          </div>
                          <div>
                            <strong>Place of Supply (POS):</strong>{' '}
                            {shipping.Stcd || '-'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Value summary */}
                    <div className='row mb-3'>
                      <div className='col-md-12'>
                        <div className='table-responsive'>
                          <Table className='table table-sm mb-0'>
                            <tbody>
                              <tr>
                                <th style={{ width: 200 }}>Taxable Value</th>
                                <td>₹ {money(taxable)}</td>
                                <th style={{ width: 200 }}>IGST</th>
                                <td>₹ {money(igst)}</td>
                              </tr>
                              <tr>
                                <th>CGST</th>
                                <td>₹ {money(cgst)}</td>
                                <th>SGST</th>
                                <td>₹ {money(sgst)}</td>
                              </tr>
                              <tr>
                                <th>Total Invoice Value</th>
                                <td>
                                  <strong>₹ {money(totalValue)}</strong>
                                </td>
                                <th>Rounded</th>
                                <td>
                                  <strong>{money(rounded)}</strong>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </div>

                    {/* Error / IRN messages (if any) */}
                    {hasError && (
                      <div className='mb-3'>
                        <div className='alert alert-warning small mb-1'>
                          <strong>GST Portal Errors:</strong>
                        </div>
                        <pre
                          className='small bg-light p-2 rounded'
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {inv.errorMessage || inv.gstzenResponse?.message || ''}
                        </pre>

                        {inv.gstzenResponse?.ErrorDetails &&
                          inv.gstzenResponse.ErrorDetails.length > 0 && (
                            <ul className='small mb-0'>
                              {inv.gstzenResponse.ErrorDetails.map((e, i) => (
                                <li key={i}>
                                  [{e.ErrorCode}] {e.ErrorMessage}
                                </li>
                              ))}
                            </ul>
                          )}
                      </div>
                    )}

                    {/* Item table */}
                    <div className='table-responsive'>
                      <Table
                        className='table table-sm table-bordered mb-0'
                        responsive
                      >
                        <thead className='table-light'>
                          <tr>
                            <th style={{ width: 50 }}>Sl</th>
                            <th>Description</th>
                            <th style={{ width: 120 }}>HSN</th>
                            <th style={{ width: 80 }}>UOM</th>
                            <th style={{ width: 80 }}>Qty</th>
                            <th style={{ width: 120 }}>Unit Price</th>
                            <th style={{ width: 120 }}>Gross Amt</th>
                            <th style={{ width: 120 }}>Discount</th>
                            <th style={{ width: 80 }}>GST %</th>
                            <th style={{ width: 130 }}>Taxable</th>
                            <th style={{ width: 130 }}>Line Total</th>
                          </tr>
                        </thead>
                        <tbody>{renderItemLines(inv)}</tbody>
                      </Table>
                    </div>
                    {(inv?.gstzenRequestPayload?.ItemList || []).length > 0 && (
                      <div className='flex justify-end mt-2 gap-2'>
                        {/* Sync Tally button */}
                        {(() => {
                          const invId = String(inv._id || inv.id)
                          const syncState = syncTallyState[invId] || {}
                          return (
                            <>
                              {!inv.isSyncToTally && (
                                <button
                                  className='btn btn-danger'
                                  disabled={syncState.loading}
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleSyncTally(inv)
                                  }}
                                  title='Sync this invoice to Tally'
                                >
                                  {syncState.loading ? (
                                    <><span className='spinner-border spinner-border-sm me-1' role='status' aria-hidden='true'></span>Syncing…</>
                                  ) : (
                                    <>Sync To Tally</>
                                  )}
                                </button>
                              )}
                              {syncState.success && (
                                <span className='text-success small align-self-center'>
                                  ✅ {syncState.success}
                                </span>
                              )}
                              {syncState.error && (
                                <span className='text-danger small align-self-center'>
                                  ❌ {syncState.error}
                                </span>
                              )}
                            </>
                          )
                        })()}

                        {/* Show "Create E-Invoice" button only for proforma invoices */}
                        {inv.isProformaInvoice === true && (
                          <button
                            className='btn btn-success'
                            onClick={e => {
                              e.stopPropagation()
                              openConvertModal(inv)
                            }}
                          >
                            Create E-Invoice
                          </button>
                        )}

                        {/* Credit Note button — only for generated non-proforma invoices {inv.isProformaInvoice !== true && status === 'generated' && ( */}
                        {inv.isProformaInvoice !== true && status === 'generated' && (
                          <button
                            className='btn btn-warning text-dark'
                            onClick={e => {
                              e.stopPropagation()
                              openCreditNoteModal(inv)
                            }}
                            title='Create a Credit Note (CRN) against this invoice'
                          >
                            Credit Note
                          </button>
                        )}

                        {/* Show "Create E-way Bill" button for non-proforma invoices */}
                        {inv.isProformaInvoice !== true && (
                          <button
                            className='btn btn-primary'
                            onClick={e => {
                              e.stopPropagation()
                              openEwayBillModal(inv)
                            }}
                            title='Generate E-way Bill for this invoice'
                          >
                            <i className='bi bi-truck me-1'></i>
                            Create E-way Bill
                          </button>
                        )}

                        {status === 'generated' && (
                          <button
                            className='btn btn-danger'
                            onClick={e => {
                              e.stopPropagation()
                              openCancelModal(inv)
                            }}
                          >
                            Cancel
                          </button>
                        )}

                        <button
                          className='btn add-btn text-white'
                          onClick={e => {
                            e.stopPropagation()
                            // Check if it's an export invoice
                            const supTyp = inv?.gstzenRequestPayload?.TranDtls?.SupTyp
                            if (supTyp === 'EXPWP' || supTyp === 'EXPWOP') {
                              handleDownloadExportInvoice(inv)
                            } else {
                              handleDownloadInvoicePdf(inv, 'quadruplicate')
                            }
                          }}
                        >
                          Quadruplicate
                        </button>

                        <button
                          className='btn add-btn text-white'
                          onClick={e => {
                            e.stopPropagation()
                            // Check if it's an export invoice
                            const supTyp = inv?.gstzenRequestPayload?.TranDtls?.SupTyp
                            if (supTyp === 'EXPWP' || supTyp === 'EXPWOP') {
                              handleDownloadExportInvoice(inv)
                            } else {
                              handleDownloadInvoicePdf(inv, 'triplicate')
                            }
                          }}
                        >
                          Triplicate
                        </button>

                        <button
                          className='btn add-btn text-white'
                          onClick={e => {
                            e.stopPropagation()
                            // Check if it's an export invoice
                            const supTyp = inv?.gstzenRequestPayload?.TranDtls?.SupTyp
                            if (supTyp === 'EXPWP' || supTyp === 'EXPWOP') {
                              handleDownloadExportInvoice(inv)
                            } else {
                              handleDownloadInvoicePdf(inv, 'duplicate')
                            }
                          }}
                        >
                          Duplicate
                        </button>

                        <button
                          className='btn add-btn text-white'
                          onClick={e => {
                            e.stopPropagation()
                            // Check if it's an export invoice
                            const supTyp = inv?.gstzenRequestPayload?.TranDtls?.SupTyp
                            if (supTyp === 'EXPWP' || supTyp === 'EXPWOP') {
                              handleDownloadExportInvoice(inv)
                            } else {
                              handleDownloadInvoicePdf(inv, 'original')
                            }
                          }}
                        >
                          Original
                        </button>

                        <button
                          className='btn add-btn text-white'
                          onClick={e => {
                            e.stopPropagation()
                            // Check if it's an export invoice
                            const supTyp = inv?.gstzenRequestPayload?.TranDtls?.SupTyp
                            if (supTyp === 'EXPWP' || supTyp === 'EXPWOP') {
                              handleDownloadExportInvoice(inv)
                            } else {
                              handleDownloadInvoicePdf(inv, '')
                            }
                          }}
                        >
                          Print
                        </button>
                      </div>
                    )}
                  </AccordionBody>
                </AccordionItem>
              )
            })}
          </Accordion>
        )}

        {totalPages > 1 && (
          <div className='d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2'>
            <div className='text-muted small'>
              Showing {Math.min((page - 1) * pageSize + 1, invoices.length)}–
              {Math.min(page * pageSize, invoices.length)} of {invoices.length}
            </div>
            <nav>
              <ul className='pagination pagination-sm mb-0'>
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className='page-link' onClick={() => setPage(1)}>«</button>
                </li>
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className='page-link' onClick={() => setPage(p => p - 1)}>‹</button>
                </li>
                {pageNums()[0] > 1 && (
                  <li className='page-item disabled'><span className='page-link'>…</span></li>
                )}
                {pageNums().map(n => (
                  <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
                    <button className='page-link' onClick={() => setPage(n)}>{n}</button>
                  </li>
                ))}
                {pageNums().at(-1) < totalPages && (
                  <li className='page-item disabled'><span className='page-link'>…</span></li>
                )}
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button className='page-link' onClick={() => setPage(p => p + 1)}>›</button>
                </li>
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button className='page-link' onClick={() => setPage(totalPages)}>»</button>
                </li>
              </ul>
            </nav>
          </div>
        )}

        {/* Cancel Invoice Modal */}
        <Modal
          isOpen={cancelModalOpen}
          toggle={closeCancelModal}
          size='lg'
          centered
        >
          <ModalHeader toggle={closeCancelModal}>Cancel Invoice</ModalHeader>
          <ModalBody>
            <div className='mb-3'>
              <label className='form-label'>
                Reason for cancellation <span className='text-danger'>*</span>
              </label>
              <textarea
                className='form-control'
                rows='3'
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder='Enter reason for cancelling this invoice'
              />
            </div>
            {cancelError && (
              <div className='alert alert-danger py-1 small mb-0'>
                {cancelError}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color='secondary'
              onClick={closeCancelModal}
              disabled={cancelLoading}
            >
              Close
            </Button>
            <Button
              color='danger'
              onClick={handleConfirmCancel}
              disabled={cancelLoading}
            >
              {cancelLoading ? (
                <>
                  <Spinner animation='border' size='sm' /> Cancelling...
                </>
              ) : (
                'Confirm Cancel'
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Convert to E-Invoice Modal */}
        <Modal
          isOpen={convertModalOpen}
          toggle={closeConvertModal}
          size='lg'
          centered
        >
          <ModalHeader toggle={closeConvertModal}>
            Convert Proforma Invoice to E-Invoice
          </ModalHeader>
          <ModalBody>
            <div className='mb-3'>
              <label className='form-label fw-semibold'>Invoice Number</label>
              <div className='form-control bg-light text-muted'>
                {convertInvoiceNumber || <em>Not assigned yet</em>}
              </div>
              <small className='text-muted'>
                This invoice number will be used to generate the official
                e-invoice and IRN from the GST portal.
              </small>
            </div>
            <p className='text-secondary small'>
              Clicking <strong>Create E-Invoice</strong> will convert this
              regular invoice to e-invoice.
            </p>
            {convertError && (
              <div className='alert alert-danger py-1 small mb-0'>
                {convertError}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color='secondary'
              onClick={closeConvertModal}
              disabled={convertLoading}
            >
              Close
            </Button>
            <Button
              color='success'
              onClick={handleConfirmConvert}
              disabled={convertLoading}
            >
              {convertLoading ? (
                <>
                  <Spinner animation='border' size='sm' /> Converting...
                </>
              ) : (
                'Create E-Invoice'
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Credit Note Confirmation Modal */}
        <Modal isOpen={crnModalOpen} toggle={closeCreditNoteModal} size='xl' centered>
          <ModalHeader toggle={closeCreditNoteModal}>Create Credit Note</ModalHeader>
          <ModalBody>
            {crnSuccess ? (
              <div className='alert alert-success mb-0'>{crnSuccess}</div>
            ) : (
              <>
                {/* Invoice summary */}
                <div className='mb-3 p-3 bg-light rounded border'>
                  <div className='row g-2 small'>
                    <div className='col-md-4'>
                      <strong>Invoice No.:</strong>{' '}
                      {crnInvoice?.invoiceNumber || crnInvoice?.gstzenRequestPayload?.DocDtls?.No || '-'}
                    </div>
                    <div className='col-md-4'>
                      <strong>Buyer:</strong>{' '}
                      {crnInvoice?.gstzenRequestPayload?.BuyerDtls?.LglNm || '-'}
                    </div>
                    <div className='col-md-4'>
                      <strong>GSTIN:</strong>{' '}
                      {crnInvoice?.gstzenRequestPayload?.BuyerDtls?.Gstin || '-'}
                    </div>
                  </div>
                </div>

                {/* Line item selection */}
                <p className='fw-semibold mb-2'>Select line items to include in the Credit Note:</p>
                <div className='table-responsive mb-3' style={{ maxHeight: 380, overflowY: 'auto' }}>
                  <table className='table table-sm table-bordered align-middle mb-0'>
                    <thead className='table-light sticky-top'>
                      <tr>
                        <th style={{ width: 44 }}>
                          {/* Select-all checkbox */}
                          <input
                            type='checkbox'
                            id='crn-select-all'
                            className='form-check-input'
                            checked={Object.keys(crnLineItems).length > 0 && Object.values(crnLineItems).every(v => v.selected)}
                            onChange={e => {
                              const checked = e.target.checked
                              setCrnLineItems(prev => {
                                const updated = { ...prev }
                                Object.keys(updated).forEach(k => { updated[k] = { ...updated[k], selected: checked } })
                                return updated
                              })
                            }}
                            title='Select / deselect all'
                          />
                        </th>
                        <th style={{ width: 40 }}>#</th>
                        <th>Description</th>
                        <th style={{ width: 80 }}>HSN</th>
                        <th style={{ width: 70 }}>UOM</th>
                        <th style={{ width: 80 }}>Invoice Qty</th>
                        <th style={{ width: 110 }}>Unit Price</th>
                        <th style={{ width: 130 }}>Credit Amount</th>
                        <th style={{ width: 120 }}>Credit Qty</th>
                        <th style={{ width: 80 }} className='text-center' title='Check to set qty as 0 (value-only credit)'>Zero Qty</th>
                        <th style={{ width: 70 }}>GST%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(crnLineItems).length === 0 ? (
                        <tr>
                          <td colSpan={11} className='text-center text-muted py-3'>No line items found in this invoice.</td>
                        </tr>
                      ) : (
                        Object.entries(crnLineItems).map(([key, { selected, qty, maxQty, amount, zeroQty, item }]) => {
                          const unitPrice = Number(item.UnitPrice || 0)
                          const invoiceTotAmt = getInvoiceLineTotAmt(item)
                          const maxAmount = +invoiceTotAmt.toFixed(2)
                          return (
                            <tr key={key} className={selected ? '' : 'table-secondary opacity-50'}>
                              <td className='text-center'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={selected}
                                  onChange={e => {
                                    const checked = e.target.checked
                                    setCrnLineItems(prev => ({
                                      ...prev,
                                      [key]: { ...prev[key], selected: checked }
                                    }))
                                  }}
                                />
                              </td>
                              <td className='text-center small'>{item.SlNo ?? key}</td>
                              <td className='small'>{item.PrdDesc || '-'}</td>
                              <td className='small text-center'>{item.HsnCd || '-'}</td>
                              <td className='small text-center'>{item.Unit === 'Project' ? 'Nos' : (item.Unit || '-')}</td>
                              <td className='small text-center fw-semibold'>{maxQty}</td>
                              {/* Unit Price */}
                              <td className='small text-end'>₹ {money(unitPrice)}</td>
                              {/* Credit Amount — from invoice TotAmt; when zeroQty mode, qty stays locked */}
                              <td>
                                <input
                                  type='number'
                                  className={`form-control form-control-sm text-end${Number(amount) > maxAmount ? ' border-danger' : ''}`}
                                  style={{ minWidth: 110 }}
                                  value={amount ?? ''}
                                  min={0}
                                  step='0.01'
                                  disabled={!selected}
                                  onChange={e => {
                                    const newAmt = e.target.value === '' ? '' : Number(e.target.value)
                                    // When zeroQty is active, qty is locked — don't recalculate it from amount.
                                    // When invoice has qty, scale qty from amount vs invoice TotAmt (not UnitPrice).
                                    const newQty = zeroQty
                                      ? qty
                                      : (maxQty > 0 && maxAmount > 0 && newAmt !== '')
                                        ? +((newAmt / maxAmount * maxQty).toFixed(3))
                                        : qty
                                    setCrnLineItems(prev => ({
                                      ...prev,
                                      [key]: { ...prev[key], amount: newAmt, qty: newQty }
                                    }))
                                  }}
                                  placeholder={`Max ₹${maxAmount}`}
                                />
                                {Number(amount) > maxAmount && (
                                  <div className='text-danger' style={{ fontSize: 10 }}>
                                    Exceeds max (₹{maxAmount})
                                  </div>
                                )}
                              </td>
                              {/* Credit Qty — disabled (not editable) when zeroQty; always shows real qty */}
                              <td>
                                <input
                                  type='number'
                                  className={`form-control form-control-sm text-center${!zeroQty && Number(qty) > maxQty ? ' border-danger' : ''}`}
                                  style={{ minWidth: 80, background: zeroQty ? '#f8f9fa' : undefined }}
                                  value={qty ?? ''}
                                  min={0}
                                  step='any'
                                  disabled={!selected || zeroQty}
                                  onChange={e => {
                                    const newQty = e.target.value === '' ? '' : Number(e.target.value)
                                    // Scale amount from invoice TotAmt by qty ratio — never UnitPrice * Qty
                                    const newAmt = (maxQty > 0 && newQty !== '')
                                      ? +((newQty / maxQty) * maxAmount).toFixed(2)
                                      : (newQty === '' ? '' : maxAmount)
                                    setCrnLineItems(prev => ({
                                      ...prev,
                                      [key]: { ...prev[key], qty: newQty, amount: newAmt }
                                    }))
                                  }}
                                  placeholder={`Max ${maxQty}`}
                                />
                                {!zeroQty && Number(qty) > maxQty && (
                                  <div className='text-danger' style={{ fontSize: 10 }}>
                                    Exceeds max ({maxQty})
                                  </div>
                                )}
                              </td>
                              {/* Zero Qty toggle */}
                              <td className='text-center'>
                                <div className='d-flex flex-column align-items-center gap-1'>
                                  <input
                                    type='checkbox'
                                    className='form-check-input'
                                    id={`zero-qty-${key}`}
                                    checked={!!zeroQty}
                                    disabled={!selected}
                                    title='Lock qty to 0 — only amount will be credited'
                                    onChange={e => {
                                      const isZero = e.target.checked
                                      setCrnLineItems(prev => {
                                        const line = prev[key]
                                        const lineMaxAmt = getInvoiceLineTotAmt(line.item)
                                        const lineMaxQty = line.maxQty
                                        return {
                                          ...prev,
                                          [key]: {
                                            ...line,
                                            zeroQty: isZero,
                                            // Keep the real qty in state — only the display is locked to 0.
                                            // When disabling zero-qty, restore qty from amount vs TotAmt (not UnitPrice).
                                            qty: !isZero
                                              ? (lineMaxQty > 0 && lineMaxAmt > 0 && line.amount !== '')
                                                ? +((Number(line.amount) / lineMaxAmt * lineMaxQty).toFixed(3))
                                                : lineMaxQty
                                              : line.qty  // preserve existing real qty
                                          }
                                        }
                                      })
                                    }}
                                  />
                                  <label
                                    htmlFor={`zero-qty-${key}`}
                                    className='text-muted'
                                    style={{ fontSize: 9, cursor: 'pointer', userSelect: 'none', lineHeight: 1.1 }}
                                  >
                                    Zero<br/>Qty
                                  </label>
                                </div>
                              </td>
                              <td className='small text-center'>{Number(item.GstRt || 0)}%</td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Selected count badge */}
                <p className='text-secondary small mb-0'>
                  <span className='badge bg-warning text-dark me-2'>
                    {Object.values(crnLineItems).filter(v => v.selected).length} item(s) selected
                  </span>
                  The credit note will be saved as <strong>active</strong> (no e-invoice yet).
                  You can then convert it to an e-invoice from the <strong>View CRN</strong> tab.
                </p>
                {crnError && (
                  <div className='alert alert-danger py-1 small mt-3 mb-0'>{crnError}</div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color='secondary' onClick={closeCreditNoteModal} disabled={crnLoading}>
              {crnSuccess ? 'Done' : 'Cancel'}
            </Button>
            {!crnSuccess && (
              <Button color='warning' className='text-dark' onClick={handleConfirmCreateCreditNote} disabled={crnLoading}>
                {crnLoading ? (
                  <><Spinner animation='border' size='sm' /> Creating...</>
                ) : (
                  'Confirm Create Credit Note'
                )}
              </Button>
            )}
          </ModalFooter>
        </Modal>

        {/* E-way Bill Modal */}
        <CreateEwayBillModal
          isOpen={ewayBillModalOpen}
          toggle={closeEwayBillModal}
          invoice={selectedInvoiceForEwayBill}
          onSuccess={handleEwayBillSuccess}
        />
      </CardBody>
    </div>
  )
}

export default ViewInvoice
